<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	// Constants
	import { Status as MarketStatus, StatusName } from '@/Constants/Market.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { CurrencyFormat } from '@/Services/Helpers.js';
	// Partials
	import Bonus from '@/Partials/Sport/Markets/_Bonus.svelte';
	import Multis from '@/Partials/Sport/Markets/_Multi.svelte';
	import ManualResultToggle from '@/Partials/Sport/Markets/_ManualResultToggle.svelte';
	import Boost from '@/Partials/Sport/Markets/_Boost.svelte';

	export let event;
	export let market;
	export let showManualResult;
	export let resultMarket;
	export let resultEvent;
	export let marketToUnresult;
	export let showPromptUnresultMarket;
	export let liabilityAdjustmentMarket;
	export let showLiabilityAdjustment;
	export let contextMenuOpen;
	export let eventClassification;

	const marketCanBeUnresulted = (market) => {
		return market && (market.resulted_at != null ? dayjs().diff(dayjs(market.resulted_at), 'hour') < 48 : true); //The race is within 48 hours of starting
	};

	const isMarketResulted = (market) => market && market.status.id === MarketStatus.Resulted;

	function onManualResultClick(market) {
		resultMarket = market;
		resultEvent = event;
		showManualResult = true;
		contextMenuOpen = undefined;
	}

	function getCurrentLiabilityLimit(market) {
		if (market.liabilities && market.liabilities.settings && market.liabilities.settings.selection_limit) {
			return CurrencyFormat(market.liabilities.settings.selection_limit);
		}
		if (eventClassification && eventClassification.selection_liability_limit !== null) {
			return CurrencyFormat(eventClassification.selection_liability_limit);
		}

		return '';
	}

	function getCurrentMultiLiabilityLimit(market) {
		if (market.liabilities && market.liabilities.settings && market.liabilities.settings.multi_selections_limit) {
			return CurrencyFormat(market.liabilities.settings.multi_selections_limit);
		}
		if (eventClassification && eventClassification.multi_selections_liability_limit !== null) {
			return CurrencyFormat(eventClassification.multi_selections_liability_limit);
		}

		return '';
	}

	function overrideStatus(market, statusId) {
		if (market.override_status && market.override_status.id === statusId) statusId = null;
		MarketStore.updateMarketOverrideStatus(market.id, statusId)
			.then(() => {
				notify({
					type: 'success',
					title: market.name,
					message: statusId
						? `Market Status Override updated to ${StatusName[statusId]}`
						: 'Market Status Override has been removed',
					timeout: 3
				});
				//update the markets selections
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Override Status',
					timeout: 5
				});
			});
	}
</script>

<div class="absolute left-0 z-40 w-52 origin-top-left rounded-md shadow-lg">
	<div
		class="py-1 bg-white rounded-md shadow-xs market_context_menu"
		role="menu"
		aria-orientation="vertical"
		aria-labelledby="user-menu"
	>
		<div class="flex flex-col justify-between h-full space-y-1">
			<div class="flex flex-col justify-center h-full space-y-1 text-sm px-1">
				{#if market.manual_result && market.status.id !== MarketStatus.Resulted}
					<div
						class="flex justify-center text-tiny"
						class:h-3={!market.is_manual}
						class:h-6={(market.manual_result && market.status.id !== MarketStatus.Resulted) || market.is_manual}
						class:my-1={market.is_manual}
					>
						{#if (market.manual_result && market.status.id !== MarketStatus.Resulted) || market.is_manual}
							<button
								on:click={() => onManualResultClick(market)}
								class="flex items-center justify-center bg-danger btn btn-primary-action w-full"
								class:text-tiny={!market.is_manual}
							>
								{#if market.is_manual}
									Result
								{:else}
									Manual Result
								{/if}
							</button>
						{/if}
					</div>
				{/if}
				{#if !isMarketResulted(market)}
					{#if !market.is_manual}
						<span class="mb-1 font-semibold text-center"> Override Status </span>
					{/if}
					<button
						on:click={() => overrideStatus(market, MarketStatus.Closed)}
						class="flex items-center justify-center py-1 bg-red-500 btn btn-primary-action"
					>
						{#if !market.is_manual}
							{#if market.override_status && market.override_status.id === MarketStatus.Closed}
								<Icon name="lock" classes="w-4 h-4 mr-1 text-red-800" />
							{:else}
								<Icon name="unlock" classes="w-4 h-4 mr-1" />
							{/if}
						{/if}
						<span class="flex-1">Close</span>
					</button>
					<button
						on:click={() => overrideStatus(market, MarketStatus.Open)}
						class="flex items-center justify-center py-1 bg-green-500 btn btn-primary-action"
					>
						{#if !market.is_manual}
							{#if market.override_status && market.override_status.id === MarketStatus.Open}
								<Icon name="lock" classes="w-4 h-4 mr-1 text-green-800" />
							{:else}
								<Icon name="unlock" classes="w-4 h-4 mr-1" />
							{/if}
						{/if}
						<span class="flex-1">Open</span>
					</button>
					<button
						on:click={() => overrideStatus(market, MarketStatus.Suspended)}
						class="flex items-center justify-center py-1 bg-orange-500 btn btn-primary-action"
					>
						{#if !market.is_manual}
							{#if market.override_status && market.override_status.id === MarketStatus.Suspended}
								<Icon name="lock" classes="w-4 h-4 mr-1 text-orange-800" />
							{:else}
								<Icon name="unlock" classes="w-4 h-4 mr-1" />
							{/if}
						{/if}
						<span class="flex-1">Suspend</span>
					</button>
					<div class="mt-2 border-b" />
				{:else if marketCanBeUnresulted(market)}
					<button
						on:click={() => {
							marketToUnresult = market;
							showPromptUnresultMarket = true;
							contextMenuOpen = undefined;
						}}
						title="Unresult market"
						class="flex items-center justify-center py-1 bg-red-500 btn btn-primary-action"
					>
						<Icon name="unfinalise" classes="block w-5 h-5 text-white inline" />
						<span class="flex-1">Unresult</span>
					</button>
				{:else}
					<button
						title="Can not unresult market"
						class="flex items-center justify-center py-1 btn btn-primary-action bg-red-500 hover:bg-red-500 opacity-40"
					>
						<Icon name="unfinalised-locked" classes="block w-5 h-5 text-white inline" />
						<span class="flex-1">Unresult</span>
					</button>
				{/if}
				{#if !isMarketResulted(market)}
					{#if !market.is_manual}
						<ManualResultToggle {market} />
					{/if}
					<Boost {market} />
					<Bonus {market} />
					<Multis {market} />
				{/if}
				{#if market?.type?.selection_win_limit_factor}
					<div
						class="flex flex-shrink hover:border-orange-700 border-white border-2 hover:bg-orange-300 cursor-pointer my-2 select-none"
					>
						Market Factor: {market.type.selection_win_limit_factor}
					</div>
				{/if}
				<div
					class="flex flex-shrink hover:border-blue-700 border-white border-2 hover:bg-blue-300 cursor-pointer my-2 select-none"
					on:click|stopPropagation={() => {
						liabilityAdjustmentMarket = market;
						showLiabilityAdjustment = true;
						contextMenuOpen = undefined;
					}}
				>
					Liability Limit: {getCurrentLiabilityLimit(market)}
				</div>
				<div
					class="flex flex-shrink hover:border-blue-700 border-white border-2 hover:bg-blue-300 cursor-pointer my-2 select-none"
					on:click|stopPropagation={() => {
						liabilityAdjustmentMarket = market;
						showLiabilityAdjustment = true;
						contextMenuOpen = undefined;
					}}
				>
					Multi Liability Limit: {getCurrentMultiLiabilityLimit(market)}
				</div>
			</div>
		</div>
	</div>
</div>
